import { marked } from 'marked';
import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';
import { sanitizeHtml } from '../../utils/sanitize';

/**
 * Markdown ⇄ HTML bridge for the Handbook WYSIWYG editor.
 *
 * Pages are stored as GitHub-flavoured Markdown; the editor works on HTML.
 * `markdownToHtml` feeds a page into the editor, `htmlToMarkdown` turns the
 * editor's HTML back into Markdown on every change. The output is normalised
 * (ATX headings, `-` bullets, `**bold**`, `*italic*`, fenced code, `---`
 * rules, tight lists) so that a page written in that style round-trips
 * byte-for-byte.
 */

export function markdownToHtml(markdown: string): string {
  if (!markdown.trim()) return '';
  return sanitizeHtml(
    marked.parse(markdown, { gfm: true, headerIds: false, mangle: false }),
  );
}

const isElement = (node: Node | null): node is HTMLElement =>
  node !== null && node.nodeType === Node.ELEMENT_NODE;

const CELL_TAGS = new Set(['TD', 'TH']);

const unwrap = (el: Element): void => {
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  el.remove();
};

/**
 * Editors wrap list-item and table-cell text in `<p>`; drop the wrapper so
 * lists stay tight and cells stay single-line, and give GFM tables a header
 * row (the only kind of table GFM can express).
 */
function normaliseEditorHtml(root: HTMLElement): void {
  root.querySelectorAll('colgroup').forEach((el) => el.remove());

  root.querySelectorAll('table').forEach((table) => {
    const firstRow = table.querySelector('tr');
    if (!firstRow || table.querySelector('thead')) return;
    Array.from(firstRow.children).forEach((cell) => {
      if (cell.nodeName === 'TH') return;
      const th = root.ownerDocument.createElement('th');
      while (cell.firstChild) th.appendChild(cell.firstChild);
      cell.replaceWith(th);
    });
  });

  root.querySelectorAll('td > p, th > p').forEach((p) => {
    if (isElement(p.nextElementSibling)) {
      p.parentNode?.insertBefore(
        root.ownerDocument.createElement('br'),
        p.nextSibling,
      );
    }
    unwrap(p);
  });

  // Only a single leading paragraph is unwrapped: an item with several
  // paragraphs is a loose item and keeps its blank lines.
  root.querySelectorAll('li').forEach((li) => {
    const blocks = Array.from(li.children);
    const paragraphs = blocks.filter((c) => c.nodeName === 'P');
    if (paragraphs.length === 1 && blocks[0] === paragraphs[0]) {
      unwrap(paragraphs[0]);
    }
  });
}

function createTurndown(): TurndownService {
  const service = new TurndownService({
    headingStyle: 'atx',
    bulletListMarker: '-',
    codeBlockStyle: 'fenced',
    emDelimiter: '*',
    strongDelimiter: '**',
    hr: '---',
  });
  service.use(gfm);

  // Turndown pads list markers to a 4-column indent (`-   item`); use the
  // single-space form and indent nested content by the marker width.
  service.addRule('listItem', {
    filter: 'li',
    replacement: (content, node, options) => {
      const parent = node.parentNode;
      let prefix = `${options.bulletListMarker} `;
      if (isElement(parent) && parent.nodeName === 'OL') {
        const start = parent.getAttribute('start');
        const index = Array.prototype.indexOf.call(parent.children, node);
        prefix = `${(start ? Number(start) : 1) + index}. `;
      }
      const indent = ' '.repeat(prefix.length);
      const body = content
        .replace(/^\n+/, '')
        .replace(/\n+$/, '\n')
        .split('\n')
        .map((line, i) => (i === 0 || line === '' ? line : indent + line))
        .join('\n');
      const trailing = node.nextSibling && !/\n$/.test(body) ? '\n' : '';
      return prefix + body + trailing;
    },
  });

  service.addRule('strikethrough', {
    filter: (node) => ['DEL', 'S', 'STRIKE'].includes(node.nodeName),
    replacement: (content) => `~~${content}~~`,
  });

  // A line break inside a table cell has to stay on one line in GFM.
  service.addRule('cellLineBreak', {
    filter: (node) =>
      node.nodeName === 'BR' &&
      isElement(node.parentNode) &&
      CELL_TAGS.has(node.parentNode.nodeName),
    replacement: () => '<br>',
  });

  return service;
}

let turndown: TurndownService | null = null;

export function htmlToMarkdown(html: string): string {
  if (!html.trim()) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  normaliseEditorHtml(doc.body);
  if (!doc.body.textContent?.trim() && !doc.body.querySelector('hr, table')) {
    return '';
  }
  turndown = turndown ?? createTurndown();
  return turndown.turndown(doc.body);
}
